import { ColorFilter } from "./V2/ColorFinderPanel"

type Range = { min: number; max: number }

export const getComprehensive15BitRange = (
    hexInput: string,
    hueRange: Range,
    satRange: Range,
    briRange: Range
): ColorFilter => {
    const { r, g, b } = hexToRgb(hexInput)

    // 1. Calculate Base HSV locally to ensure accuracy
    const { h: baseHue, s: baseSat, v: baseBri } = rgbToHsv(r, g, b)

    // 2. Calculate Range Boundaries
    // Sat/Bri ranges are 0-100 relative to a center of 50
    // Example: Range{40, 60} means -10 to +10 from base
    const sMin = clamp(baseSat + (satRange.min - 50) * 2, 0, 100)
    const sMax = clamp(baseSat + (satRange.max - 50) * 2, 0, 100)
    const vMin = clamp(baseBri + (briRange.min - 50) * 2, 0, 100)
    const vMax = clamp(baseBri + (briRange.max - 50) * 2, 0, 100)

    const hOffsetMin = Math.floor(hueRange.min - 50)
    const hOffsetMax = Math.ceil(hueRange.max - 50)

    const colorSet = new Set<number>()

    // 3. Iterate
    // Optimization: 15-bit color (RGB555) discards lower 3 bits of 8-bit channels.
    // Changes of < 3% in Sat/Bri often map to the same 15-bit key.
    // Stepping by 2 or 3 reduces loop count significantly with minimal loss of output resolution.
    for (let h = hOffsetMin; h <= hOffsetMax; h++) {
        const effectiveHue = (baseHue + h + 720) % 360

        for (let s = sMin; s <= sMax; s += 2) {
            for (let v = vMin; v <= vMax; v += 2) {
                const rgb = hsvToRgb(effectiveHue, s, v)

                // RGB555 Pack: (R << 10) | (G << 5) | B
                // R, G, B are 0-255. We need top 5 bits.
                const r5 = (rgb.r >> 3) & 0x1f
                const g5 = (rgb.g >> 3) & 0x1f
                const b5 = (rgb.b >> 3) & 0x1f

                const colorKey = (r5 << 10) | (g5 << 5) | b5
                colorSet.add(colorKey)
            }
        }
    }

    return colorSet
}

// --- ROBUST UTILS ---

const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(val, max))

const hexToRgb = (hex: string) => {
    hex = hex.replace(/^#/, "")
    if (hex.length === 3)
        hex = hex
            .split("")
            .map((c) => c + c)
            .join("")
    const num = parseInt(hex, 16)
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}

/**
 * Converts RGB (0-255) to HSV (H:0-360, S:0-100, V:0-100)
 */
const rgbToHsv = (r: number, g: number, b: number) => {
    const rN = r / 255,
        gN = g / 255,
        bN = b / 255
    const max = Math.max(rN, gN, bN),
        min = Math.min(rN, gN, bN)
    const d = max - min

    let h = 0
    const s = max === 0 ? 0 : d / max
    const v = max

    if (max !== min) {
        switch (max) {
            case rN:
                h = (gN - bN) / d + (gN < bN ? 6 : 0)
                break
            case gN:
                h = (bN - rN) / d + 2
                break
            case bN:
                h = (rN - gN) / d + 4
                break
        }
        h /= 6
    }

    return {
        h: h * 360,
        s: s * 100,
        v: v * 100,
    }
}

/**
 * Converts HSV to RGB (0-255)
 */
const hsvToRgb = (h: number, s: number, v: number) => {
    const sN = s / 100
    const vN = v / 100

    const c = vN * sN
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = vN - c

    let r = 0,
        g = 0,
        b = 0

    if (h >= 0 && h < 60) {
        r = c
        g = x
        b = 0
    } else if (h >= 60 && h < 120) {
        r = x
        g = c
        b = 0
    } else if (h >= 120 && h < 180) {
        r = 0
        g = c
        b = x
    } else if (h >= 180 && h < 240) {
        r = 0
        g = x
        b = c
    } else if (h >= 240 && h < 300) {
        r = x
        g = 0
        b = c
    } else {
        r = c
        g = 0
        b = x
    }

    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255),
    }
}

export const getColorRangeBounds = (
    hexInput: string,
    hueRange: Range,
    satRange: Range,
    briRange: Range
): { from: string; to: string } => {
    const { r, g, b } = hexToRgb(hexInput)
    const { h: baseHue, s: baseSat, v: baseBri } = rgbToHsv(r, g, b)

    const sMin = clamp(baseSat + (satRange.min - 50) * 2, 0, 100)
    const sMax = clamp(baseSat + (satRange.max - 50) * 2, 0, 100)

    const vMin = clamp(baseBri + (briRange.min - 50) * 2, 0, 100)
    const vMax = clamp(baseBri + (briRange.max - 50) * 2, 0, 100)

    const hOffsetMin = hueRange.min - 50
    const hOffsetMax = hueRange.max - 50

    const hMin = (baseHue + hOffsetMin + 720) % 360
    const hMax = (baseHue + hOffsetMax + 720) % 360

    const fromRGB = hsvToRgb(hMin, sMin, vMin)
    const toRGB = hsvToRgb(hMax, sMax, vMax)

    return {
        from: rgbToHex(fromRGB.r, fromRGB.g, fromRGB.b),
        to: rgbToHex(toRGB.r, toRGB.g, toRGB.b),
    }
}

const rgbToHex = (r: number, g: number, b: number): string => {
    return (
        "#" +
        [r, g, b]
            .map((x) => {
                const hex = x.toString(16)
                return hex.length === 1 ? "0" + hex : hex
            })
            .join("")
    )
}
